import React, { useEffect } from 'react';
import {
  StyledFixedBanner,
  StyledLeafletHub,
  StyledMenuBanner,
} from './styles';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGetHubInit, apiGetLeafletMenu } from 'src/api/apiLeaflet';
import { E_QUERY_KEY } from 'src/utils/queryKey';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { E_LINK_KIND } from 'src/utils/enum';
import { bridgeOpenBrowser } from 'src/utils/bridge';
import FixedImage from './components/FixedImage';
import { IS_LEAFLET_TYPE, LEAFLET_URL_CHECK } from 'src/utils/constants';
import { leafletGaEvent, leafletGaPageView } from 'src/utils/ga';
import { apiSetLogging } from 'src/api/apiAssistant';
import { TConnLoggingInfo } from 'src/types/types';
import { appStoreMove, changeLeafletUrl } from 'src/utils/leaflet';
import { getInnerLinkData } from 'src/utils/utility';

/**
 * @name 전단 허브
 */
function LeafletHub(): React.ReactElement {
  // 전단 허브 조회
  const { data: leafletHub } = useQuery(
    E_QUERY_KEY.PRODUCT_LIST_FIX_LEAFLET_LIST,
    apiGetHubInit,
  );
  // 전단 메뉴 조회
  const { data: leafletMenu } = useQuery(
    E_QUERY_KEY.PRODUCT_LIST_LEAFLETMEMU,
    apiGetLeafletMenu,
    {
      select: data => data.leaflet_menu_list,
    },
  );

  const pageName = '전단허브';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const setConnLog = useMutation(apiSetLogging); // 외부진입 로그

  // 전단 고정 배너 클릭
  const handleClickFixedBanner = banner => {
    leafletGaEvent(pageName, `전단허브배너 고정(${banner.str_type})`);
    navigate(`/leaflet?rst1=${banner.str_type}`);
  };

  // 전단 메뉴 배너 클릭
  const handleClickMenuBanner = menu => {
    // 연결없음 클릭방지
    if (menu.link_type === E_LINK_KIND.NONE) return;
    // GA 수집
    leafletGaEvent(pageName, `전단허브배너 테마(${menu.leaflet_menu_nm})`);
    if (menu.link_type === E_LINK_KIND.INSIDE) {
      if (IS_LEAFLET_TYPE) {
        // 앱전단 : 모든 루트 가능
        const innerLinkData = getInnerLinkData(menu.link_url);
        navigate(innerLinkData.url, { replace: innerLinkData.replace });
      } else {
        // 웹전단 : 메인, /leaflet pathname에 쿼리스트링 있을 경우만 내부이동
        if (menu.link_url === '/' || menu.link_url.includes('/leaflet?')) {
          navigate(menu.link_url);
        } else if (menu.link_url.includes('?leaflet_id=')) {
          navigate(`/leaflet${menu.link_url}`);
        } else {
          const qrParams = menu.link_url.includes('?') ? '&' : '?';
          const url =
            process.env.REACT_APP_GO_URL +
            menu.link_url +
            qrParams +
            'hfrom=APP&rst1=J';
          appStoreMove(url);
        }
      }
    } else if (menu.link_type === E_LINK_KIND.OUTSIDE) {
      if (LEAFLET_URL_CHECK(menu.link_url)) {
        //전단웹url인 경우 내부url로 변환해서 페이지 이동
        navigate(changeLeafletUrl(menu.link_url));
      } else {
        bridgeOpenBrowser(menu.link_url);
      }
    } else return;
  };

  // 외부진입 로그 수집
  useEffect(() => {
    const hfrom = searchParams.get('hfrom') || '';
    const paramList: TConnLoggingInfo = { hfrom: hfrom };
    [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
      item => (paramList[`option${item}`] = searchParams.get(`rst${item}`)),
    );
    let _pageName = pageName;
    if (!IS_LEAFLET_TYPE) {
      // 웹 전단 로그수집
      if (hfrom) {
        // 로그 구분값이 있으면 수집
        setConnLog.mutate({
          ...paramList,
        });
      }
    }
    if (paramList.option2) {
      // 외부진입구분값 있을 시 GA 데이터 추가
      // GA 수집가능 길이를 고려해 수집데이터 한자리만 추가
      _pageName =
        pageName +
        `(${paramList.option2})(${paramList.option3 ? paramList.option3 : ''})`;
    }
    leafletGaPageView(_pageName, location.pathname);

    if (!IS_LEAFLET_TYPE) {
      // 데이터 수집 후 파라미터 삭제
      window.history.replaceState('', '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location]);
  return (
    <>
      <FixedImage />
      <StyledLeafletHub>
        <StyledFixedBanner>
          {leafletHub &&
            leafletHub.plate_leaflet_str_list.map(banner => {
              return (
                <li
                  key={banner.str_type}
                  onClick={() => handleClickFixedBanner(banner)}
                  role="button"
                >
                  <img src={banner.img_url} alt={banner.img_desc} />
                </li>
              );
            })}
        </StyledFixedBanner>
        <StyledMenuBanner>
          {leafletMenu &&
            leafletMenu.map(menu => {
              return (
                <li
                  key={menu.leaflet_menu_id}
                  role="button"
                  onClick={() => handleClickMenuBanner(menu)}
                >
                  <img
                    src={menu.leaflet_menu_img_url}
                    alt={menu.leaflet_menu_img_desc}
                  />
                </li>
              );
            })}
        </StyledMenuBanner>
      </StyledLeafletHub>
    </>
  );
}

export default LeafletHub;

/**
 * @name 전단 고정 목록
 * @description 테스트용 점포코드
 */
export const FIXED_LEAFLT = [
  {
    str_type: '1',
    str_type_nm: 'HYPER',
    str_cd: '301',
    title: '롯데마트 전단 대상점포:롯데마트 오프라인 매장',
  },
  {
    str_type: '2',
    str_type_nm: 'ZETTA',
    str_cd: '302',
    title:
      '롯데마트 제타플렉스 전단 대상점포:롯데마트 제타플렉스 잠실, 서울역점',
  },
  {
    str_type: '3',
    str_type_nm: 'MAXX',
    str_cd: '645',
    title:
      '롯데마트 맥스 전단 대상점포:롯데마트맥스 금천, 목포, 상무, 영등포, 창원중앙점',
  },
  {
    str_type: '4',
    str_type_nm: 'SUPER',
    str_cd: 'ltsp',
    title: '롯데슈퍼 전단 대상점포: 롯데슈퍼 오프라인 매장',
  },
];
